/**
 * Firm Contacts datatable. Click a row to open the detail Sheet.
 *
 * Rows can be grouped by category or type (`groupBy`, owned by the view's
 * toolbar). Grouping inserts a collapsible section header above each run of
 * rows; sections follow the canonical category/type order while rows inside
 * a section keep whatever sort the user picked.
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Row,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { Building2, ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import type { Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";
import { ColumnVisibilityMenu } from "@/ui/composed/data-display/column-visibility-menu";
import { buildFirmContactsColumns } from "./columns";
import {
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  firmContactState,
  resolveFirmCategoryDisplay,
  type ContactType,
  type FirmContactCategory,
  type FirmContactCustomCategory,
  type FirmContactRow,
} from "./types";
import { AU_STATE_VALUES } from "@/utils/format/auStates";

/** Toolbar grouping modes. */
export type FirmContactGroupBy =
  "none" | "category" | "type" | "state" | "firm";

/** Group key for contacts whose addresses carry no state (sorts last). */
const NO_STATE_KEY = "__none__";
/** Group key for rows that don't nest under a firm (MOR-995). */
const NO_FIRM_KEY = "__nofirm__";

interface GroupSection {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  rows: Array<Row<FirmContactRow>>;
}

const STICKY_HEAD =
  "sticky left-0 z-20 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] min-w-[280px]";
const STICKY_CELL =
  "sticky left-0 z-10 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] min-w-[280px]";

const STORAGE_KEY = "firm-contacts-hidden-cols";

function loadVisibility(): VisibilityState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as VisibilityState;
  } catch {
    /* ignore */
  }
  return {};
}

interface FirmContactsDataTableProps {
  rows: Array<FirmContactRow>;
  selectedFirmContactId: Id<"firmContacts"> | null;
  onSelect: (row: FirmContactRow) => void;
  /** Grouping mode; owned by FirmContactsView's toolbar select. */
  groupBy?: FirmContactGroupBy;
  /** Org-defined custom categories (MOR-914) for badge/group resolution. */
  customCategories?: ReadonlyArray<FirmContactCustomCategory>;
  /**
   * Bulk selection (MOR-915), mirroring the precedent library. When
   * `selectable`, a leading checkbox column is rendered; while at least one
   * row is selected, row clicks toggle selection instead of opening the
   * detail panel. State is owned by FirmContactsView.
   */
  selectable?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
  /** Select/deselect many at once (header + group checkboxes). */
  onSetSelected?: (ids: string[], selected: boolean) => void;
}

export function FirmContactsDataTable({
  rows,
  selectedFirmContactId,
  onSelect,
  groupBy = "none",
  customCategories = [],
  selectable = false,
  selectedIds,
  onToggleSelect,
  onSetSelected,
}: FirmContactsDataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "displayName", desc: false },
  ]);
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(loadVisibility);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(columnVisibility),
      );
    } catch {
      /* ignore */
    }
  }, [columnVisibility]);

  const customCategoriesById = useMemo(
    () => new Map(customCategories.map((c) => [String(c._id), c])),
    [customCategories],
  );

  const columns = useMemo(
    () => buildFirmContactsColumns(customCategoriesById),
    [customCategoriesById],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const selectionActive = selectable && (selectedIds?.size ?? 0) > 0;
  const visibleColCount =
    table.getVisibleFlatColumns().length + (selectable ? 1 : 0);
  const sortedRows = table.getRowModel().rows;

  const allVisibleSelected =
    selectable &&
    sortedRows.length > 0 &&
    sortedRows.every((r) => selectedIds?.has(r.original._id));
  const someVisibleSelected = sortedRows.some((r) =>
    selectedIds?.has(r.original._id),
  );

  /** Sections that the user has collapsed, keyed by group key. */
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const sections = useMemo<Array<GroupSection> | null>(() => {
    if (groupBy === "none") return null;
    const buckets = new Map<string, Array<Row<FirmContactRow>>>();
    for (const row of sortedRows) {
      const key =
        groupBy === "category"
          ? resolveFirmCategoryDisplay(row.original, customCategoriesById).key
          : groupBy === "type"
            ? row.original.contactType
            : groupBy === "firm"
              ? row.original.parentFirmContactId
                ? String(row.original.parentFirmContactId)
                : NO_FIRM_KEY
              : (firmContactState(row.original) ?? NO_STATE_KEY);
      const list = buckets.get(key) ?? [];
      list.push(row);
      buckets.set(key, list);
    }
    // Canonical order first, then anything unrecognised, so sections don't
    // reshuffle as the user re-sorts the rows inside them. Category grouping
    // appends the org's custom categories (in their sort order) after the
    // built-ins; state grouping sorts unknown states alphabetically and puts
    // "No state" last.
    const order: ReadonlyArray<string> =
      groupBy === "category"
        ? [
            ...FIRM_CATEGORY_ORDER,
            ...[...customCategories]
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((c) => `custom:${c._id}`),
          ]
        : groupBy === "type"
          ? CONTACT_TYPE_ORDER
          : groupBy === "firm"
            ? []
            : AU_STATE_VALUES;
    const firmLabel = (key: string) =>
      buckets.get(key)?.[0]?.original.parentDisplayName ?? key;
    const unrecognised = Array.from(buckets.keys()).filter(
      (k) => !order.includes(k) && k !== NO_STATE_KEY && k !== NO_FIRM_KEY,
    );
    if (groupBy === "state") unrecognised.sort();
    // Firm grouping has no canonical order — alphabetical by firm name,
    // with contacts that don't belong to a firm last.
    if (groupBy === "firm") {
      unrecognised.sort((a, b) => firmLabel(a).localeCompare(firmLabel(b)));
    }
    const keys = [
      ...order.filter((k) => buckets.has(k)),
      ...unrecognised,
      ...(buckets.has(NO_STATE_KEY) ? [NO_STATE_KEY] : []),
      ...(buckets.has(NO_FIRM_KEY) ? [NO_FIRM_KEY] : []),
    ];
    return keys.map((key) => {
      if (groupBy === "category") {
        const custom = key.startsWith("custom:")
          ? customCategoriesById.get(key.slice("custom:".length))
          : undefined;
        if (custom) {
          const sample = buckets.get(key)?.[0]?.original;
          const display = sample
            ? resolveFirmCategoryDisplay(sample, customCategoriesById)
            : null;
          return {
            key,
            label: custom.name,
            icon: display?.icon,
            rows: buckets.get(key) ?? [],
          };
        }
        const meta = FIRM_CATEGORY_META[key as FirmContactCategory];
        return {
          key,
          label: meta?.label ?? key,
          icon: meta?.icon,
          rows: buckets.get(key) ?? [],
        };
      }
      if (groupBy === "firm") {
        return {
          key,
          label: key === NO_FIRM_KEY ? "No firm" : firmLabel(key),
          icon: key === NO_FIRM_KEY ? undefined : Building2,
          rows: buckets.get(key) ?? [],
        };
      }
      const meta =
        groupBy === "type" ? CONTACT_TYPE_META[key as ContactType] : null;
      return {
        key,
        label: meta?.label ?? (key === NO_STATE_KEY ? "No state" : key),
        icon: meta?.icon,
        rows: buckets.get(key) ?? [],
      };
    });
  }, [sortedRows, groupBy, customCategories, customCategoriesById]);

  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderContactRow = (row: Row<FirmContactRow>) => {
    const isSelected = selectedFirmContactId === row.original._id;
    const isChecked = selectedIds?.has(row.original._id) ?? false;
    return (
      <TableRow
        key={row.id}
        className={cn(
          "cursor-pointer transition-colors",
          isSelected || isChecked
            ? "hover:bg-primary/8 bg-primary/5"
            : "hover:bg-muted/50",
        )}
        onClick={() => {
          // While a bulk selection is active, row clicks extend it instead
          // of opening the detail panel (mirrors the precedent library).
          if (selectionActive && onToggleSelect) {
            onToggleSelect(row.original._id);
            return;
          }
          onSelect(row.original);
        }}
      >
        {selectable && (
          <TableCell
            className="w-10 py-3 pl-4 pr-0 align-top"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => onToggleSelect?.(row.original._id)}
              aria-label={`Select ${row.original.displayName}`}
            />
          </TableCell>
        )}
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(
              "px-4 py-3 align-top",
              cell.column.id === "jobsUsing" && "text-right",
              cell.column.id === "displayName" && STICKY_CELL,
              cell.column.id === "displayName" && isSelected && "!bg-primary/5",
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-end px-1">
        <ColumnVisibilityMenu table={table} />
      </div>
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        <div className="max-h-[calc(100vh-300px)] overflow-auto">
          <Table className="w-full min-w-[900px]">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="bg-muted/30 hover:bg-transparent"
                >
                  {selectable && (
                    <TableHead className="h-10 w-10 pl-4 pr-0">
                      <Checkbox
                        checked={
                          allVisibleSelected
                            ? true
                            : someVisibleSelected
                              ? "indeterminate"
                              : false
                        }
                        onCheckedChange={() =>
                          onSetSelected?.(
                            sortedRows.map((r) => r.original._id),
                            !allVisibleSelected,
                          )
                        }
                        aria-label="Select all contacts"
                      />
                    </TableHead>
                  )}
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                        header.id === "jobsUsing" && "text-right",
                        header.id === "displayName" && STICKY_HEAD,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColCount}
                    className="h-32 text-center"
                  >
                    <span className="text-sm text-muted-foreground">
                      No firm contacts match the current filters.
                    </span>
                  </TableCell>
                </TableRow>
              ) : sections ? (
                sections.map((section) => {
                  const isCollapsed = collapsed.has(section.key);
                  const Chevron = isCollapsed ? ChevronRight : ChevronDown;
                  const Icon = section.icon;
                  return (
                    <Fragment key={section.key}>
                      <TableRow
                        className="cursor-pointer bg-muted/60 hover:bg-muted/80"
                        onClick={() => toggleSection(section.key)}
                      >
                        {selectable && (
                          <TableCell
                            className="w-10 py-2 pl-4 pr-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={
                                section.rows.length > 0 &&
                                section.rows.every((r) =>
                                  selectedIds?.has(r.original._id),
                                )
                                  ? true
                                  : section.rows.some((r) =>
                                        selectedIds?.has(r.original._id),
                                      )
                                    ? "indeterminate"
                                    : false
                              }
                              onCheckedChange={() =>
                                onSetSelected?.(
                                  section.rows.map((r) => r.original._id),
                                  !section.rows.every((r) =>
                                    selectedIds?.has(r.original._id),
                                  ),
                                )
                              }
                              aria-label={`Select all in ${section.label}`}
                            />
                          </TableCell>
                        )}
                        <TableCell
                          colSpan={visibleColCount - (selectable ? 1 : 0)}
                          className="px-4 py-2"
                        >
                          {/* Sticky so the section label survives horizontal
                              scroll, matching the sticky name column. */}
                          <span className="sticky left-0 flex w-fit items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            <Chevron className="size-3.5 shrink-0" />
                            {Icon && <Icon className="size-3.5 shrink-0" />}
                            {section.label}
                            <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
                              {section.rows.length}
                            </span>
                          </span>
                        </TableCell>
                      </TableRow>
                      {!isCollapsed && section.rows.map(renderContactRow)}
                    </Fragment>
                  );
                })
              ) : (
                sortedRows.map(renderContactRow)
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
